import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  FolderOpen,
  Plus,
  Loader2,
  Facebook,
  Mail,
  Calendar,
  Eye,
  FileText,
  Copy
} from 'lucide-react';
import { buildCampaignPrefill } from '../../utils/campaignPrefill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  changes_requested: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const PLATFORM_ICONS = {
  facebook: Facebook,
  email: Mail
};

function CampaignsList() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const handleDuplicate = async (campaignId) => {
    if (!campaignId) return;
    setDuplicatingId(campaignId);
    try {
      const response = await axios.post(`${API}/campaigns/${campaignId}/duplicate`);
      const newCampaign = response.data.campaign;
      const newId = response.data.campaign_id;
      toast.success('Campaign duplicated');
      navigate('/campaigns/create', {
        state: buildCampaignPrefill(newCampaign, {
          notice: 'Loaded a copy — tweak and submit when ready.'
        })
      });
      return newId;
    } catch (error) {
      console.error('Duplicate campaign error:', error);
      toast.error('Failed to duplicate campaign');
    } finally {
      setDuplicatingId(null);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Load campaigns error:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">All Campaigns</h1>
          <p className="text-white/60">View and manage all your campaigns</p>
        </div>
        <Button
          onClick={() => navigate('/campaigns/create')}
          className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <Card className="card">
          <CardContent className="p-12 text-center">
            <FolderOpen className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Campaigns Yet</h3>
            <p className="text-white/60 mb-4">Create your first campaign to get started</p>
            <Button
              onClick={() => navigate('/campaigns/create')}
              className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign, idx) => {
            const PlatformIcon = PLATFORM_ICONS[campaign.platform] || FileText;
            
            return (
              <Card key={idx} className="card hover:border-white/20 transition-all cursor-pointer"
                onClick={() => navigate(`/campaigns/${campaign.campaign_id || idx}`)}>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-[#00d4aa]/20 rounded-lg">
                      <PlatformIcon className="w-6 h-6 text-[#00d4aa]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-white truncate">
                          {campaign.campaignName || 'Untitled Campaign'}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[campaign.status]}`}>
                          {campaign.status?.replace('_', ' ').toUpperCase() || 'DRAFT'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-white/60">
                        <span>{campaign.clientName}</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(campaign.createdAt).toLocaleDateString()}
                        </span>
                        <span>{campaign.ads?.length || 0} ads</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={duplicatingId === campaign.campaign_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(campaign.campaign_id);
                      }}
                      title="Duplicate campaign"
                    >
                      {duplicatingId === campaign.campaign_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CampaignsList;
